import { useEffect, useState, useMemo } from 'react';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';

interface UseSessionTimerFromStartedAtResult {
  remainingSeconds: number;
  progressPercentage: number;
  isTimerActive: boolean;
}

export const useSessionTimerFromStartedAt = (
  session: DeviceConfigurationApi | undefined
): UseSessionTimerFromStartedAtResult => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Calcola la durata totale del video
  const totalDurationSeconds = useMemo(() => {
    if (!session) return 0;
    const duration =
      session.video?.duration || session.config?.maxDuration || 0;
    console.log('[useSessionTimer] Total duration calculation:', {
      videoDuration: session.video?.duration,
      configMaxDuration: session.config?.maxDuration,
      calculatedDuration: duration,
    });
    return duration;
  }, [session?.video?.duration, session?.config?.maxDuration]);

  // Determina il timestamp di inizio (started_at o fallback a created_at)
  const startTimestamp = useMemo(() => {
    if (!session) return null;
    // Usa started_at se presente, altrimenti created_at come fallback
    const timestamp = session.started_at || session.created_at || null;
    console.log('[useSessionTimer] Start timestamp:', {
      started_at: session.started_at,
      created_at: session.created_at,
      using: timestamp,
    });
    return timestamp;
  }, [session?.started_at, session?.created_at]);

  // Verifica se il timer deve essere attivo
  const isTimerActive = useMemo(() => {
    console.log('[useSessionTimer] Checking if timer should be active:', {
      hasSession: !!session,
      status: session?.status,
      startTimestamp,
      totalDurationSeconds,
    });

    if (!session) {
      console.log('[useSessionTimer] ❌ No session');
      return false;
    }
    if (session.status !== 'in-progress') {
      console.log(
        '[useSessionTimer] ❌ Status is not in-progress:',
        session.status
      );
      return false;
    }
    if (!startTimestamp) {
      console.log(
        '[useSessionTimer] ❌ No start timestamp (neither started_at nor created_at)'
      );
      return false;
    }
    if (totalDurationSeconds <= 0) {
      console.log(
        '[useSessionTimer] ❌ No duration or duration <= 0:',
        totalDurationSeconds
      );
      return false;
    }
    console.log('[useSessionTimer] ✅ Timer should be active');
    return true;
  }, [session?.status, startTimestamp, totalDurationSeconds]);

  // Calcola il tempo rimanente e il progress
  const { remainingSeconds, progressPercentage } = useMemo(() => {
    if (!isTimerActive || !startTimestamp) {
      return { remainingSeconds: 0, progressPercentage: 0 };
    }

    try {
      const startedAtTime = new Date(startTimestamp).getTime();

      // Verifica che startTimestamp sia una data valida
      if (isNaN(startedAtTime)) {
        console.log('[useSessionTimer] ❌ Invalid start timestamp');
        return { remainingSeconds: 0, progressPercentage: 0 };
      }

      const elapsedSeconds = Math.max(0, (currentTime - startedAtTime) / 1000);
      const remaining = totalDurationSeconds - elapsedSeconds;

      // Progress: se la sessione è durata più del previsto, mostra 100%
      const progress =
        totalDurationSeconds > 0
          ? Math.min(
              100,
              Math.max(0, (elapsedSeconds / totalDurationSeconds) * 100)
            )
          : 0;

      return {
        remainingSeconds: Math.max(0, remaining),
        progressPercentage: progress,
      };
    } catch (error) {
      // Gestisce errori di parsing della data
      console.error('Error calculating session timer:', error);
      return { remainingSeconds: 0, progressPercentage: 0 };
    }
  }, [isTimerActive, startTimestamp, currentTime, totalDurationSeconds]);

  // Aggiorna il timer ogni secondo quando è attivo
  useEffect(() => {
    if (!isTimerActive) return;

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerActive]);

  return {
    remainingSeconds,
    progressPercentage,
    isTimerActive,
  };
};
